import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { OM_LOGO, MTN_LOGO, OPERATOR_COLORS } from "../assets/logos";
import Card, { SectionLabel } from "../components/ui/Card";
import Button from "../components/ui/Button";
import { fmt, calcFees, genRef, nowLabel } from "../utils/helpers";

export default function Transfer() {
  const navigate = useNavigate();
  const { setPendingTx } = useApp();

  const [dir, setDir] = useState("om"); // "om" = Orange→MTN | "mtn" = MTN→Orange
  const [srcPhone, setSrcPhone] = useState("");
  const [dstPhone, setDstPhone] = useState("");
  const [amount, setAmount] = useState("");

  const srcOp = dir === "om" ? "o" : "m";
  const dstOp = dir === "om" ? "m" : "o";
  const srcCol = OPERATOR_COLORS[srcOp];
  const dstCol = OPERATOR_COLORS[dstOp];

  const amt = parseFloat(amount) || 0;
  const fees = calcFees(amt);
  const total = amt + fees;

  const handleContinue = () => {
    const { date, time } = nowLabel();
    setPendingTx({
      type: "transfer",
      payOp: srcOp,
      recOp: dstOp,
      payPhone: srcPhone || "6XX XXX XXX",
      recPhone: dstPhone || "6XX XXX XXX",
      amount: amt || 0,
      fees,
      total,
      ref: genRef(),
      date,
      time,
      label: `Transfert ${srcCol.nameShort}→${dstCol.nameShort}`,
      from: "/transfer",
    });
    navigate("/confirm");
  };

  return (
    <div className="bg-[#f0f2f7] pb-2 screen-enter">
      {/* Header */}
      <div className="bg-brand-blue px-[17px] pt-[14px] pb-[18px]">
        <button
          onClick={() => navigate("/")}
          className="bg-transparent border-none text-white/85 text-xs cursor-pointer p-0 flex items-center gap-1 mb-[9px] font-medium tap-highlight"
        >
          <i className="fa fa-arrow-left" /> Retour
        </button>
        <h2 className="text-white text-[17px] font-bold mb-0.5">Transfert Mobile Money</h2>
        <p className="text-white/80 text-[11px]">Précisez les deux numéros et le montant</p>
      </div>

      <div className="p-3">
        {/* Step 1 : Direction */}
        <Card className="mb-[9px]">
          <SectionLabel>1 · Direction du transfert</SectionLabel>
          <div className="flex flex-col gap-1.5">
            {[
              { key: "om", srcLogo: OM_LOGO, srcH: "17px", label: "Orange Money → MTN MoMo", col: OPERATOR_COLORS["o"] },
              { key: "mtn", srcLogo: MTN_LOGO, srcH: "15px", label: "MTN MoMo → Orange Money", col: OPERATOR_COLORS["m"] },
            ].map(({ key, srcLogo, srcH, label, col: c }) => {
              const sel = dir === key;
              return (
                <button
                  key={key}
                  onClick={() => setDir(key)}
                  className="cursor-pointer border-2 rounded-[13px] p-[11px] bg-white flex items-center gap-2.5 tap-highlight transition-all"
                  style={{ borderColor: sel ? c.primary : "#e5e7eb" }}
                >
                  <img src={srcLogo} alt="" style={{ height: srcH }} className="flex-shrink-0" />
                  <span className="text-[11px] font-bold text-brand-dark flex-1 text-left">{label}</span>
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: sel ? c.primary : "#e5e7eb" }}
                  >
                    <i className="fa fa-check text-white" style={{ fontSize: 7 }} />
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Step 2 : Numéro source */}
        <Card className="mb-[9px]">
          <SectionLabel>2 · Numéro source · {srcCol.name}</SectionLabel>
          <div className="flex gap-1.5">
            <div
              className="flex items-center px-3 rounded-[9px] text-xs font-bold flex-shrink-0 border"
              style={{ background: srcCol.light, color: srcCol.primary, borderColor: srcCol.border }}
            >
              +237
            </div>
            <input
              type="tel"
              placeholder="6XX XXX XXX"
              value={srcPhone}
              onChange={(e) => setSrcPhone(e.target.value)}
              className="flex-1 border border-gray-200 rounded-[11px] px-3 py-[11px] text-sm outline-none focus:border-brand-orange bg-white"
            />
          </div>
        </Card>

        {/* Step 3 : Numéro destinataire */}
        <Card className="mb-[9px]">
          <SectionLabel>3 · Numéro destinataire · {dstCol.name}</SectionLabel>
          <div className="flex gap-1.5">
            <div
              className="flex items-center px-3 rounded-[9px] text-xs font-bold flex-shrink-0 border"
              style={{ background: dstCol.light, color: dstCol.primary, borderColor: dstCol.border }}
            >
              +237
            </div>
            <input
              type="tel"
              placeholder="6XX XXX XXX"
              value={dstPhone}
              onChange={(e) => setDstPhone(e.target.value)}
              className="flex-1 border border-gray-200 rounded-[11px] px-3 py-[11px] text-sm outline-none focus:border-brand-orange bg-white"
            />
          </div>
        </Card>

        {/* Step 4 : Montant */}
        <Card className="mb-3">
          <SectionLabel>4 · Montant (FCFA)</SectionLabel>
          <input
            type="number"
            placeholder="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full border border-gray-200 rounded-[11px] px-3 py-[11px] text-xl font-bold outline-none focus:border-brand-orange bg-white mb-2.5"
          />
          <div className="bg-gray-50 rounded-[10px] p-2.5 border border-gray-100">
            <div className="flex justify-between mb-1.5">
              <span className="text-xs text-gray-500">Montant</span>
              <span className="text-xs font-semibold text-brand-dark">{fmt(amt)}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-xs text-gray-500">Frais (1%)</span>
              <span className="text-xs font-semibold text-amber-700">{fmt(fees)}</span>
            </div>
            <div className="border-t border-gray-100 pt-1.5 flex justify-between">
              <span className="text-xs font-bold text-brand-dark">Total débité</span>
              <span className="text-[13px] font-bold text-brand-blue">{fmt(total)}</span>
            </div>
          </div>
        </Card>

        <Button fullWidth variant="blue" onClick={handleContinue}>
          Continuer &nbsp;<i className="fa fa-arrow-right" />
        </Button>
      </div>
    </div>
  );
}
